<template>
  <div class="columns">
    <v-btn-group variant="outlined" divided>
      <v-menu :close-on-content-click="false">
        <template #activator="{ props: activatorProps }">
          <v-btn
            append-icon="mdi-arrow-down"
            color="primary"
            v-bind="activatorProps"
          >
            Feedback-LED
          </v-btn>
        </template>
        <v-card max-width="500px">
          <v-card-text>
            <v-text-field
              v-model="configRef.FeedbackIntensity.value"
              :label="`Helligkeit: ${configRef.FeedbackIntensity.value} %`"
              class="slider is-fullwidth is-info"
              step="1"
              min="0"
              max="100"
              type="range"
            />
            <span
              >Aufleuchten der LED zwecks Quittierung des Tastendrucks.
            </span>
          </v-card-text>
        </v-card>
      </v-menu>
      <v-menu :close-on-content-click="false">
        <template #activator="{ props: activatorProps }">
          <v-btn
            append-icon="mdi-arrow-down"
            v-bind="activatorProps"
            color="primary"
          >
            <span>Funktions-LED</span>
          </v-btn>
        </template>
        <v-card max-width="500px">
          <v-card-text>
            <v-number-input
              v-model="configRef.LocatingIntensity.value"
              :label="`Helligkeit: ${configRef.LocatingIntensity.value} %`"
              :step="1"
              :min="0"
              :max="100"
              class="slider is-fullwidth is-info"
              type="range"
            />
            <v-number-input
              v-model="configRef.LocatingPeriod.value"
              :label="`Frequenz: ${configRef.LocatingPeriod.value / 10} s`"
              class="slider is-fullwidth is-info"
              :step="1"
              :min="20"
              :max="250"
              type="range"
            />
            <ContainerWithLabel label="Bei externer Speisung">
              Bei externer Speisung kann gewählt werden, ob die Funktions-LEDs
              regelmässig blinken oder dauernd leuchten sollen. Bei
              Batteriespeisung wird automatisch auf blinken umgestellt.
              <v-select
                v-model="configRef.Bitfield.value"
                :items="[
                  {
                    title: 'Blinken',
                    value: 0,
                  },
                  { title: 'Leuchten', value: 1 },
                ]"
              />
            </ContainerWithLabel>
            <span
              >Einstellungen für folgende LED-Rückmeldungen: Nacht-LED und die
              LED-Einstellungen der Funkion "Joker".</span
            >
          </v-card-text>
        </v-card>
      </v-menu>
      <v-menu :close-on-content-click="false">
        <template #activator="{ props: activatorProps }">
          <v-btn
            append-icon="mdi-arrow-down"
            v-bind="activatorProps"
            color="primary"
          >
            <span>Sendertyp</span>
          </v-btn>
        </template>
        <v-card max-width="500px">
          <v-card-text>
            <v-select
              v-model="configRef.TxType.value"
              label="Sendertyp"
              :items="[
                { value: 0, title: 'Standard' },
                { value: 1, title: 'Mastersender' },
                { value: 2, title: 'Multitasking (MT)' },
                { value: 3, title: '1-Kanalsender' },
                { value: 4, title: 'Mehrkanalsender' },
              ]"
              @update:model-value="checkUpdateLinked"
            />

            <ContainerWithLabel
              v-if="configRef.TxType.value !== 0"
              label="Senderadresse"
            >
              <!-- <TheMask
                  :model-value="txAddress"
                  :masked="true"
                  :mask="'FFFFFF'"
                  :tokens="txTokens"
                  @update:model-value="txAddressChanged"
                /> -->
              <v-text-field
                v-model="txAddress"
                :rules="[
                  (v) =>
                    v.length === 6 || 'Senderaddresse muss 6-stellig sein.',
                ]"
              />
            </ContainerWithLabel>
            <p v-if="txAddressError" style="color: red">
              Fehler: Senderaddresse muss 6-stellig sein.
            </p>
            <span
              >Falls nicht die ab Werk vorgegebene und unikatäre
              Standard-Sendernummer dieses Senders verwendet werden soll, kann
              der Sendertyp gewechselt und die gewünschte Sendernummer
              eingegeben werden. Es kann jederzeit wieder der Sendertyp
              "Standard" gewählt werden, wodurch wieder die Sendernummber ab
              Werk verwendet wird.</span
            >
          </v-card-text>
        </v-card>
      </v-menu>
    </v-btn-group>
    <ContainerWithLabel label="Neues Passwort" class="my-4">
      <!-- <TheMask
        :model-value="passwordNew"
        :masked="true"
        :mask="'FFFFFF'"
        :tokens="pwTokens"
        @update:model-value="passwordNewChanged"
      /> -->
      <v-text-field
        :model-value="passwordNew"
        @update:model-value="passwordNewChanged"
      />
      <!-- :rules="[(v) => v.length === 6 || 'Passwort muss 6-stellig sein.']" -->
      <p v-if="newPasswordError" style="color: red">
        Fehler: Passwort muss 6-stellig sein.
      </p>
    </ContainerWithLabel>
    <MaxFlexLayoutView
      v-if="layout"
      :big-view="true"
      :combined-highlight="false"
      :layout="layout"
      :config="configRef"
      @click="selectKey"
    />
    <v-btn-group variant="outlined" divided color="primary">
      <v-btn variant="elevated" @click="closeModal">Übernehmen</v-btn>
      <v-btn @click="changeLayout"> Layout wechseln </v-btn>
      <v-btn @click="cancel">Abbrechen</v-btn>
    </v-btn-group>
    <CommonDialog v-model:is-active="buttonConfigModalActive">
      <template #content>
        <MaxFlexButtonConfigEditor
          v-bind="selectedButtonConfig"
          @close="buttonConfigModalActive = false"
          @input="onButtonConfigChanged"
        />
      </template>
    </CommonDialog>
  </div>
</template>

<script setup lang="ts">
import hotkeys from 'hotkeys-js'
import isEqual from 'lodash.isequal'
import merge from 'lodash.merge'
import { computed, onBeforeMount, onMounted, onUnmounted, Ref, ref } from 'vue'
import { mask, TheMask } from 'vue-the-mask'

const vMask = mask
import {
  getMaxFlexLayoutByValue,
  LocatingKeyButtonMap,
  MaxFlexConfig,
} from '@/apps/brelag/common/models/maxflex/maxflex'
import CommonDialog from '@/components/common/CommonDialog.vue'
import ContainerWithLabel from '@/components/common/ContainerWithLabel.vue'
import { useConfirmationDialog } from '@/util/composables/useConfirmationDialog'

import MaxFlexButtonConfigEditor from './MaxFlexButtonConfigEditor.vue'
import MaxFlexLayoutView from './MaxFlexLayoutView.vue'

const emit = defineEmits(['close', 'changeLayout'])
type MaxFlexConfigEditorProps = {
  config: MaxFlexConfig
}

const props = defineProps<MaxFlexConfigEditorProps>()
const configRef: Ref<MaxFlexConfig> = ref(null)
onBeforeMount(() => {
  configRef.value = props.config
})
const txAddressError = ref(false)
const newPasswordError = ref(false)
const buttonConfigModalActive = ref(false)
const txTokens = ref({
  F: {
    pattern: /[1-8]/,
  },
})
const pwTokens = ref({
  F: {
    pattern: /[0-8]/,
  },
})

const originalConfig: Ref<MaxFlexConfig> = ref(null)
const selectedButtonConfig: Ref<{
  buttonNum: number
  config: MaxFlexConfig
}> = ref(null)

onMounted(() => {
  hotkeys('esc', { scope: 'MaxFlexConfigEditor', keyup: true }, (event) => {
    // only fire event on keyup to avoid closing confirm dialog directly
    if (event.type === 'keyup') {
      event.preventDefault()
      cancel()
    }
  })
  hotkeys.setScope('MaxFlexConfigEditor')

  // keep original config to detect changes
  originalConfig.value = JSON.parse(JSON.stringify(props.config))
})

onUnmounted(() => {
  hotkeys.unbind('esc', 'MaxFlexConfigEditor')
  hotkeys.setScope('EditModal') // TODO
})

const confirm = useConfirmationDialog()
async function cancel() {
  if (isEqual(configRef.value, originalConfig.value)) {
    // no changes, close immediatly
    closeModal()
  } else {
    const isConfirmed = await confirm({
      content: 'Sind Sie sicher, dass Sie die Änderung verwerfen möchten?',
    })
    if (!isConfirmed) return
    merge(configRef.value, originalConfig.value)
    closeModal()
  }
}

function closeModal() {
  emit('close')
}

function changeLayout() {
  emit('changeLayout')
}

const layout = computed(() => {
  return configRef.value
    ? getMaxFlexLayoutByValue(configRef.value?.KeyLayout.value)
    : null
})

const txAddress = computed({
  get() {
    return configRef.value
      ? `${configRef.value.TxAddress1.value}${configRef.value.TxAddress2.value}${configRef.value.TxAddress3.value}${configRef.value.TxAddress4.value}${configRef.value.TxAddress5.value}${configRef.value.TxAddress6.value}`
      : ''
  },
  set(newValue) {
    txAddressChanged(newValue)
  },
})

const passwordNew = computed(() => {
  return `${String.fromCharCode(
    configRef.value.PasswordNew1.value
  )}${String.fromCharCode(
    configRef.value.PasswordNew2.value
  )}${String.fromCharCode(
    configRef.value.PasswordNew3.value
  )}${String.fromCharCode(
    configRef.value.PasswordNew4.value
  )}${String.fromCharCode(
    configRef.value.PasswordNew5.value
  )}${String.fromCharCode(configRef.value.PasswordNew6.value)}`
})

function passwordNewChanged(value) {
  newPasswordError.value = false
  if (!value) {
    // value is empty -> Reset password
    configRef.value.PasswordNew1.value = 48
    configRef.value.PasswordNew2.value = 48
    configRef.value.PasswordNew3.value = 48
    configRef.value.PasswordNew4.value = 48
    configRef.value.PasswordNew5.value = 48
    configRef.value.PasswordNew6.value = 48
  } else if (value.length !== 6) {
    // We get a string of length 0 to 6 with values 0-8
    newPasswordError.value = true
    return
  } else {
    configRef.value.PasswordNew1.value = value[0].charCodeAt(0)
    configRef.value.PasswordNew2.value = value[1].charCodeAt(0)
    configRef.value.PasswordNew3.value = value[2].charCodeAt(0)
    configRef.value.PasswordNew4.value = value[3].charCodeAt(0)
    configRef.value.PasswordNew5.value = value[4].charCodeAt(0)
    configRef.value.PasswordNew6.value = value[5].charCodeAt(0)
  }
}

function txAddressChanged(value) {
  // We get a string of length 0 to 6 with values 1-8
  if (value.length !== 6) {
    txAddressError.value = true
    return
  } else {
    txAddressError.value = false
  }
  configRef.value.TxAddress1.value = parseInt(value[0])
  configRef.value.TxAddress2.value = parseInt(value[1])
  configRef.value.TxAddress3.value = parseInt(value[2])
  configRef.value.TxAddress4.value = parseInt(value[3])
  configRef.value.TxAddress5.value = parseInt(value[4])
  configRef.value.TxAddress6.value = parseInt(value[5])

  checkUpdateLinked()
}

function checkUpdateLinked() {
  if (
    configRef.value.TxAddress1.value !==
      originalConfig.value.TxAddress1.value ||
    configRef.value.TxAddress2.value !==
      originalConfig.value.TxAddress2.value ||
    configRef.value.TxAddress3.value !==
      originalConfig.value.TxAddress3.value ||
    configRef.value.TxAddress4.value !==
      originalConfig.value.TxAddress4.value ||
    configRef.value.TxAddress5.value !==
      originalConfig.value.TxAddress5.value ||
    configRef.value.TxAddress6.value !==
      originalConfig.value.TxAddress6.value ||
    (originalConfig.value.TxType.value === 0 &&
      configRef.value.TxType.value !== 0) ||
    (originalConfig.value.TxType.value !== 0 &&
      configRef.value.TxType.value === 0)
  ) {
    // TxAddress changed OR type changed away from standard OR type changed back to standard
    // -> should update linked receivers
    configRef.value.updateLinked = true
  } else {
    configRef.value.updateLinked = false
  }
}

function selectKey(layoutName: string, blockNum: number) {
  buttonConfigModalActive.value = true
  selectedButtonConfig.value = {
    buttonNum: blockNum,
    config: configRef.value,
  }
}

function onButtonConfigChanged(nightLED: boolean, buttonNum: number) {
  if (nightLED) {
    // Night LED has been turned on for this button
    configRef.value.LocatingKey.value = LocatingKeyButtonMap[buttonNum]
  } else {
    configRef.value.LocatingKey.value = 0
  }
}
</script>

<style></style>
